import { useEffect, useRef } from "react";
import { Link } from "react-router";
import { SpotlightCard, StatCard, TestimonialCard } from "../TestimonialGrid/TestimonialGrid.jsx";
import styles from "./TestimonialCarousel.module.css";

const DRAG_SLOP = 4; // px of movement before a press counts as a drag, not a click
const FRICTION = 0.94; // how fast the glide after a release runs out
const MIN_VELOCITY = 0.08; // px per frame, below which the glide stops

/** Mixed formats keep a long rail from reading as one card repeated twelve times. */
function Card({ item }) {
  if (item.spotlight) return <SpotlightCard item={item} />;
  if (item.stat) return <StatCard item={item} />;
  return <TestimonialCard item={item} clamped />;
}

/**
 * Every testimonial on one draggable rail that runs in a loop. The list is rendered
 * three times: the middle copy is what you see, and when a scroll reaches either outer
 * copy the position jumps back by one copy's width — the same cards keep coming in both
 * directions with no visible seam. Without JS it is still an ordinary scrollable row.
 */
export default function TestimonialCarousel({ items, label = "Client testimonials", more }) {
  const viewport = useRef(null);
  const drag = useRef(null);
  const glide = useRef(0);
  const copies = [...items, ...items, ...items];

  useEffect(() => {
    const el = viewport.current;
    if (!el) return;
    el.scrollLeft = el.scrollWidth / 3;
  }, [items.length]);

  useEffect(() => {
    const el = viewport.current;
    if (!el) return;

    const move = (e) => {
      const d = drag.current;
      if (!d || e.pointerId !== d.id) return;
      const dx = e.clientX - d.x;
      d.moved = Math.max(d.moved, Math.abs(dx));
      // Velocity of the last pointer step, for the glide after the release.
      d.velocity = d.lastX - e.clientX;
      d.lastX = e.clientX;
      el.scrollLeft = d.left - dx;
      if (d.moved > DRAG_SLOP) e.preventDefault();
    };
    const up = (e) => {
      const d = drag.current;
      if (!d || e.pointerId !== d.id) return;
      drag.current = null;
      el.removeAttribute("data-dragging");
      if (d.moved <= DRAG_SLOP) return;
      // Let go and it keeps running, slowing down — no snapping to a card edge.
      let v = d.velocity;
      const coast = () => {
        v *= FRICTION;
        if (Math.abs(v) < MIN_VELOCITY) return;
        el.scrollLeft += v;
        glide.current = requestAnimationFrame(coast);
      };
      cancelAnimationFrame(glide.current);
      glide.current = requestAnimationFrame(coast);
      // Swallow the click that follows the release, so dragging across a card's link
      // doesn't navigate.
      const swallow = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
      };
      window.addEventListener("click", swallow, { capture: true, once: true });
      setTimeout(() => window.removeEventListener("click", swallow, { capture: true }), 0);
    };

    window.addEventListener("pointermove", move, { passive: false });
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    return () => {
      cancelAnimationFrame(glide.current);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
  }, []);

  const onPointerDown = (e) => {
    // Touch already scrolls this natively, and better than any script can.
    if (e.pointerType === "touch") return;
    const el = viewport.current;
    cancelAnimationFrame(glide.current); // grabbing it again stops the glide
    drag.current = {
      id: e.pointerId,
      x: e.clientX,
      lastX: e.clientX,
      left: el.scrollLeft,
      moved: 0,
      velocity: 0,
    };
    el.setAttribute("data-dragging", "");
  };

  const onScroll = () => {
    const el = viewport.current;
    const copy = el.scrollWidth / 3;
    if (el.scrollLeft < copy * 0.5) el.scrollLeft += copy;
    else if (el.scrollLeft > copy * 1.5) el.scrollLeft -= copy;
  };

  const step = (direction) => {
    const el = viewport.current;
    const [a, b] = el.firstElementChild.children;
    const by = b ? b.offsetLeft - a.offsetLeft : a.offsetWidth;
    const smooth = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollBy({ left: direction * by, behavior: smooth ? "smooth" : "auto" });
  };

  return (
    <div className={styles.rail}>
      <div
        ref={viewport}
        className={styles.viewport}
        role="group"
        aria-label={label}
        // A scrollable region has to be reachable by keyboard (WCAG 2.1.1) — arrow keys
        // then scroll the rail, and the arrow buttons below move it a card at a time.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        onScroll={onScroll}
        onPointerDown={onPointerDown}
      >
        <ul className={styles.track}>
          {copies.map((item, i) => {
            const isCopy = i < items.length || i >= items.length * 2;
            return (
              <li
                key={`${item.author}-${i}`}
                className={`${styles.slide} ${item.spotlight ? styles.slideWide : ""}`.trim()}
                // The outer copies are the same quotes again: keep them out of the
                // reading order and out of the tab order.
                inert={isCopy ? true : undefined}
                aria-hidden={isCopy ? "true" : undefined}
              >
                <Card item={item} />
              </li>
            );
          })}
        </ul>
      </div>
      <div className={styles.controls}>
        {more ? (
          <Link to={more.to} className={styles.more}>
            {more.label}
          </Link>
        ) : (
          <span />
        )}
        <div className={styles.arrows}>
          <button
            type="button"
            className={styles.arrow}
            aria-label="Previous testimonials"
            onClick={() => step(-1)}
          >
            <span aria-hidden="true">&#8592;</span>
          </button>
          <button
            type="button"
            className={styles.arrow}
            aria-label="Next testimonials"
            onClick={() => step(1)}
          >
            <span aria-hidden="true">&#8594;</span>
          </button>
        </div>
      </div>
    </div>
  );
}
